// Botonera de forma de pago del detalle de cuenta de cobranza (portal de cobranza y
// detalle de cuenta del admin). La fuente es `cuentas_cobranza.tipo_financiamiento`:
//
// - Sin forma de pago: un solo botón "Forma de pago" con Recursos propios / Crédito
//   hipotecario / Ya tiene crédito hipotecario (el mismo trío que ofrece la app cliente).
// - Con forma de pago: "Forma de pago" desaparece y queda "Reiniciar financiamiento"
//   como acción de emergencia, escondida en "Más acciones". Si es crédito, ahí mismo
//   va "Actualizar crédito".
//
// La decisión es pura para que las dos pantallas muestren lo mismo; cada pantalla
// traduce sus propios permisos a esta entrada.

// FINANCIAMIENTO_EXTERNO = ni recursos propios ni crédito con un banco del portal
// Bancos (otro banco, broker externo u otro arreglo). Mismo valor que guarda la app
// cliente (EF cliente-pago-final): no abre solicitud en el portal Bancos. El equipo lo
// ve como "Ya tiene crédito hipotecario" y el cliente como "Ya cuento con crédito
// hipotecario"; el valor guardado no cambia.
export type FormaPago = 'RECURSOS_PROPIOS' | 'CREDITO_HIPOTECARIO' | 'FINANCIAMIENTO_EXTERNO';

/** Forma de pago normalizada. `otra` = valor no reconocido: solo se puede reiniciar. */
export type EstadoFormaPago = FormaPago | 'sin_definir' | 'otra';

export function estadoFormaPago(tipoFinanciamiento: string | null | undefined): EstadoFormaPago {
  const v = (tipoFinanciamiento ?? '').trim();
  if (v === '') return 'sin_definir';
  if (v === 'RECURSOS_PROPIOS' || v === 'CREDITO_HIPOTECARIO' || v === 'FINANCIAMIENTO_EXTERNO') return v;
  return 'otra';
}

export const ETIQUETA_FORMA_PAGO: Record<EstadoFormaPago, string> = {
  RECURSOS_PROPIOS: 'Recursos propios',
  CREDITO_HIPOTECARIO: 'Crédito hipotecario',
  FINANCIAMIENTO_EXTERNO: 'Ya tiene crédito hipotecario',
  sin_definir: 'No definido',
  otra: 'No reconocido',
};

export interface EntradaAccionesFinanciamiento {
  tipoFinanciamiento: string | null | undefined;
  /** Solo la cuenta de la propiedad lleva forma de pago; los accesorios la heredan. */
  esPropiedad: boolean;
  /** Bloqueo general de escritura de la pantalla (demanda, cuenta cancelada en el portal). */
  soloLectura: boolean;
  /** Cuenta cancelada: no se registra forma de pago ni crédito. */
  cancelada: boolean;
  /** Permiso para fijar la forma de pago y registrar/actualizar el crédito. */
  puedeRegistrar: boolean;
  /** Permiso para reiniciar el financiamiento. */
  puedeReiniciar: boolean;
}

export interface AccionesFinanciamiento {
  estado: EstadoFormaPago;
  /** Botón "Forma de pago" (Recursos propios / Crédito hipotecario / Ya tiene crédito hipotecario). */
  formaPago: boolean;
  /** "Reiniciar financiamiento" dentro de Más acciones. */
  reiniciar: boolean;
  /** "Actualizar crédito" dentro de Más acciones. */
  actualizarCredito: boolean;
  /** Mostrar el menú Más acciones (tiene al menos una opción). */
  masAcciones: boolean;
}

export function accionesFinanciamiento(e: EntradaAccionesFinanciamiento): AccionesFinanciamiento {
  const estado = estadoFormaPago(e.tipoFinanciamiento);
  const base = e.esPropiedad && !e.soloLectura;
  const registrar = base && !e.cancelada && e.puedeRegistrar;

  const formaPago = registrar && estado === 'sin_definir';
  const reiniciar = base && e.puedeReiniciar && estado !== 'sin_definir';
  const actualizarCredito = registrar && estado === 'CREDITO_HIPOTECARIO';

  return { estado, formaPago, reiniciar, actualizarCredito, masAcciones: reiniciar || actualizarCredito };
}

// ── Alcance de "Recursos propios" y "Ya tiene crédito hipotecario" ──────────────────────────────────────────────
// El mismo que usa el crédito (RPC credito_escriturable): cuentas activas y no hijas
// de la propiedad; la principal (oferta sin producto, la de menor id) más los
// accesorios que entran a la escritura: estacionamiento (categoría 1) y bodega
// (categoría 2). Mantenimiento y otros productos no se tocan.

export interface CuentaCandidata {
  id: number;
  activo: boolean;
  esHija: boolean;
  /** `ofertas.id_producto` de la cuenta; null = oferta de la propiedad. */
  idProducto: number | null;
  /** `productos_servicios.id_categoria` del producto; null si no hay producto. */
  idCategoria: number | null;
  /** La cuenta tiene oferta (sin oferta no entra, igual que en la RPC). */
  tieneOferta: boolean;
}

export const CATEGORIAS_ESCRITURABLES = [1, 2] as const;

export function cuentasEscriturables(cuentas: CuentaCandidata[]): number[] {
  const vivas = cuentas.filter((c) => c.activo && !c.esHija && c.tieneOferta);
  const principal = vivas
    .filter((c) => c.idProducto == null)
    .sort((a, b) => a.id - b.id)[0];
  if (!principal) return [];
  const accesorios = vivas.filter(
    (c) => c.id !== principal.id && c.idCategoria != null
      && (CATEGORIAS_ESCRITURABLES as readonly number[]).includes(c.idCategoria),
  );
  return [principal.id, ...accesorios.map((c) => c.id).sort((a, b) => a - b)];
}

import { conAvisos } from '@/lib/notificaciones/silencioAvisos';

// Cliente supabase mínimo (se recibe por parámetro para que este módulo no importe el
// cliente real y se pueda probar sin el entorno de Vite).
type Cliente = { from: (tabla: string) => any };

/** Formas de pago que solo se marcan en la cuenta (sin crédito ni solicitud). */
export type FormaPagoSimple = Exclude<FormaPago, 'CREDITO_HIPOTECARIO'>;

export interface ResultadoRecursosPropios {
  cuentaIds: number[];
  /** tipo_financiamiento previo por cuenta, para la bitácora. */
  antes: Record<number, string | null>;
}

/** Atajo de `marcarFormaPago` para recursos propios. */
export function marcarRecursosPropios(cliente: Cliente, cuentaId: number): Promise<ResultadoRecursosPropios> {
  return marcarFormaPago(cliente, cuentaId, 'RECURSOS_PROPIOS');
}

/**
 * Marca RECURSOS_PROPIOS o FINANCIAMIENTO_EXTERNO en la cuenta principal de la
 * propiedad y sus accesorios escriturables. Waterfall plano (sin embeds de PostgREST).
 */
export async function marcarFormaPago(
  cliente: Cliente, cuentaId: number, tipo: FormaPagoSimple, notificar = true,
): Promise<ResultadoRecursosPropios> {
  const { cuentaIds, antes } = await cuentasDeLaFormaPago(cliente, cuentaId);

  const { data: actualizadas, error: e4 } = await conAvisos(
    cliente.from('cuentas_cobranza').update({ tipo_financiamiento: tipo }).in('id', cuentaIds).select('id'),
    notificar,
  );
  if (e4) throw e4;
  // Un UPDATE que la policy filtra no da error: devuelve cero filas.
  if (Array.isArray(actualizadas) && actualizadas.length === 0) {
    throw new Error('No se pudo actualizar la cuenta (sin permiso o cuenta no visible)');
  }

  return { cuentaIds, antes };
}

/**
 * Reinicia el financiamiento (acción interna de emergencia): limpia `tipo_financiamiento` y da de
 * baja el crédito y la solicitud vigentes en las mismas cuentas que fija `marcarFormaPago`. Antes
 * buscaba las hermanas solo por `id_propiedad` (la bodega por oferta quedaba fuera) e ignoraba el
 * `error` de cada UPDATE, así que el aviso de éxito salía aunque la policy lo bloqueara.
 * No es atómico: si falla a medias, repetirlo es seguro (cada paso es idempotente).
 */
export async function reiniciarFinanciamiento(
  cliente: Cliente, cuentaId: number, notificar = true,
): Promise<ResultadoRecursosPropios> {
  const { cuentaIds, antes } = await cuentasDeLaFormaPago(cliente, cuentaId);

  const { data: actualizadas, error: e1 } = await conAvisos(
    cliente.from('cuentas_cobranza').update({ tipo_financiamiento: null }).in('id', cuentaIds).select('id'),
    notificar,
  );
  if (e1) throw e1;
  if (Array.isArray(actualizadas) && actualizadas.length === 0) {
    throw new Error('No se pudo actualizar la cuenta (sin permiso o cuenta no visible)');
  }

  const ahora = new Date().toISOString();
  for (const tabla of ['creditos_hipotecarios', 'bancos_solicitudes']) {
    const { error } = await cliente
      .from(tabla).update({ activo: false, fecha_actualizacion: ahora })
      .in('id_cuenta_cobranza', cuentaIds).eq('activo', true);
    if (error) throw error;
  }

  return { cuentaIds, antes };
}

/** Cuentas escriturables de la propiedad de `cuentaId` y su forma de pago actual. */
export async function cuentasDeLaFormaPago(cliente: Cliente, cuentaId: number): Promise<ResultadoRecursosPropios> {
  const { data: cuenta, error: e1 } = await cliente
    .from('cuentas_cobranza').select('id, id_propiedad, id_oferta').eq('id', cuentaId).maybeSingle();
  if (e1) throw e1;
  if (!cuenta) throw new Error('No se encontró la cuenta');

  let propId: number | null = cuenta.id_propiedad ?? null;
  if (propId == null && cuenta.id_oferta != null) {
    const { data: of, error } = await cliente
      .from('ofertas').select('id_propiedad').eq('id', cuenta.id_oferta).maybeSingle();
    if (error) throw error;
    propId = of?.id_propiedad ?? null;
  }
  if (propId == null) throw new Error('La cuenta no pertenece a una propiedad');

  // Cuentas de la propiedad: por id_propiedad directo o por la oferta de la propiedad.
  const { data: ofertasProp, error: e2 } = await cliente
    .from('ofertas').select('id').eq('id_propiedad', propId);
  if (e2) throw e2;
  const ofertaIds = (ofertasProp ?? []).map((o: { id: number }) => Number(o.id));

  const sel = 'id, activo, id_oferta, id_cuenta_cobranza_padre, tipo_financiamiento';
  const { data: porProp, error: e3 } = await cliente
    .from('cuentas_cobranza').select(sel).eq('id_propiedad', propId);
  if (e3) throw e3;
  let porOferta: unknown[] = [];
  if (ofertaIds.length) {
    const { data, error } = await cliente.from('cuentas_cobranza').select(sel).in('id_oferta', ofertaIds);
    if (error) throw error;
    porOferta = data ?? [];
  }
  const filas = new Map<number, { id: number; activo: boolean | null; id_oferta: number | null; id_cuenta_cobranza_padre: number | null; tipo_financiamiento: string | null }>();
  for (const r of [...(porProp ?? []), ...porOferta] as Array<{ id: number; activo: boolean | null; id_oferta: number | null; id_cuenta_cobranza_padre: number | null; tipo_financiamiento: string | null }>) {
    filas.set(Number(r.id), r);
  }

  // Producto y categoría de cada oferta.
  const idsOferta = [...new Set([...filas.values()].map((r) => r.id_oferta).filter((x): x is number => x != null))];
  const productoPorOferta = new Map<number, number | null>();
  if (idsOferta.length) {
    const { data, error } = await cliente.from('ofertas').select('id, id_producto').in('id', idsOferta);
    if (error) throw error;
    for (const o of data ?? []) productoPorOferta.set(Number(o.id), o.id_producto ?? null);
  }
  const idsProducto = [...new Set([...productoPorOferta.values()].filter((x): x is number => x != null))];
  const categoriaPorProducto = new Map<number, number | null>();
  if (idsProducto.length) {
    const { data, error } = await cliente.from('productos_servicios').select('id, id_categoria').in('id', idsProducto);
    if (error) throw error;
    for (const p of data ?? []) categoriaPorProducto.set(Number(p.id), p.id_categoria ?? null);
  }

  const candidatas: CuentaCandidata[] = [...filas.values()].map((r) => {
    const idProducto = r.id_oferta != null ? productoPorOferta.get(Number(r.id_oferta)) ?? null : null;
    return {
      id: Number(r.id),
      activo: r.activo !== false,
      esHija: r.id_cuenta_cobranza_padre != null,
      tieneOferta: r.id_oferta != null,
      idProducto,
      idCategoria: idProducto != null ? categoriaPorProducto.get(idProducto) ?? null : null,
    };
  });
  const cuentaIds = cuentasEscriturables(candidatas);
  if (!cuentaIds.length) throw new Error('La propiedad no tiene cuenta principal activa');

  const antes: Record<number, string | null> = {};
  for (const id of cuentaIds) antes[id] = filas.get(id)?.tipo_financiamiento ?? null;

  return { cuentaIds, antes };
}
